import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import ChartProvider from '../../../../../charts/echarts/ChartProvider'
import ChartColorProvider from '../../../../../charts/echarts/ColorProvider'
import Tooltip from '../../../../../charts/echarts/Tooltip'
import AxisX from '../../../../../charts/echarts/AxisX'
import AxisY from '../../../../../charts/echarts/AxisY'
import ComponentLine from '../../../../../charts/echarts/ComponentLine'
import { useFormattingContext } from '../../../../FormattingProvider/FormattingContext'
import { buildPointTooltipFormatter } from './trendTooltip'
import { alignSeriesToSharedAxis } from './alignSeries'

// Line chart variant of the trend tool. Multi-series capable: pass any
// number of series via `seriesList`; one ComponentLine is rendered per
// entry, sharing a single category axis (union of period endDates,
// padded with null for missing periods so each line renders as gaps
// where it has no coverage).
//
// ECharts axis layout:
//   - X axis: `type: 'category'` with pre-formatted period labels
//     (matches the bar variant). This intentionally avoids
//     `type: 'time'`: AxisX's `replaceFormatter` always wraps
//     axisLabel.formatter into a function, but echarts' time-axis
//     internals treat that field as a string template and call
//     `.replace()` on it -- which throws for function-typed
//     formatters. Category axis sidesteps the issue entirely.
//   - Y axis: `type: 'value'` formatted via the (shared-measureKind)
//     primary series's valueFormat.

export default function TrendLineChart ({ seriesList, valueFormat, frequency, height = 340 }) {
  const { formatter: fmt } = useFormattingContext()

  const { categories, alignedSeries } = useMemo(
    () => alignSeriesToSharedAxis(seriesList, frequency),
    [seriesList, frequency]
  )

  // Per-series value format -- used by the tooltip to render each
  // series in its own units when (in the rare case) a chart mixes
  // formatters even within a shared measureKind. Keyed by both
  // seriesId and seriesName so the formatter can resolve either.
  const seriesValueFormats = useMemo(() => {
    const map = {}
    for (const s of alignedSeries) {
      if (!s) continue
      if (s.id) map[s.id] = s.valueFormat || valueFormat
      if (s.name) map[s.name] = s.valueFormat || valueFormat
    }
    return map
  }, [alignedSeries, valueFormat])

  const tooltipFormatter = useMemo(
    () => buildPointTooltipFormatter(fmt, valueFormat, frequency, seriesValueFormats),
    [fmt, valueFormat, frequency, seriesValueFormats]
  )

  return (
    <ChartColorProvider theme='default'>
      <ChartProvider height={height} loadingTemplate='axis' emptyTemplate='axis'>
        <Tooltip trigger='axis' formatter={tooltipFormatter} />
        <AxisX
          type='category'
          data={categories}
          format={null}
          axisLabel={{ hideOverlap: true, fontSize: 10 }}
          splitLine={{ show: false }}
        />
        <AxisY
          type='value'
          format={valueFormat || null}
          axisLabel={{ fontSize: 10 }}
          splitLine={{ lineStyle: { color: 'var(--opal-border-default, #eee)' } }}
        />
        {alignedSeries.map(s => (
          <ComponentLine
            key={s.id}
            seriesId={s.id}
            name={s.name}
            data={s.data}
            showSymbol={false}
            smooth={false}
            connectNulls={false}
            lineStyle={{ width: 2 }}
          />
        ))}
      </ChartProvider>
    </ChartColorProvider>
  )
}

TrendLineChart.propTypes = {
  seriesList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    valueFormat: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      value: PropTypes.number
    }))
  })).isRequired,
  valueFormat: PropTypes.string,
  frequency: PropTypes.string,
  height: PropTypes.number
}
